import { NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TopBarComponent } from './top-bar/top-bar.component';
import { SvgIconComponent } from './svg-icon/svg-icon.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { ApiService } from './api.service';
import { BadUrlComponent } from './bad-url/bad-url.component';
import { DetailsBoxComponent } from './eeoc/details-box/details-box.component';
import { ClickOutsideModule } from 'ng-click-outside';
import { SuccessPageComponent } from './eeoc/success-page/success-page.component';
import { FooterComponent } from './footer/footer.component';
import { EeocInfoBarComponent } from './eeoc/eeoc-info-bar/eeoc-info-bar.component';
import { NumberValidationDirective } from './shared/directives/number-validation.directive';
import { JobPreferencesComponent } from './job-preferences/job-preferences.component';
import { JobPreferencesSuccessPageComponent } from './job-preferences/job-preferences-success-page/job-preferences-success-page.component';
import { APP_BASE_HREF, CommonModule, DatePipe, PlatformLocation } from '@angular/common';
import { AuthGuard } from './service/auth.guard';
import { AuthService } from './service/auth.service';
import { NameFormatPipe } from './shared/pipes/name-format.pipe';
import { StorageService } from './service/storage.service';
import { LoginPageComponent } from './login-page/login-page.component';
import { LoaderComponent } from './loader/loader.component';
import { AppStateService } from './service/app-state.service';
import { NavigationService } from './service/navigation.service';
import { AuthRequiredGuard } from './service/auth-required.guard';
import { RedirectGuard } from './service/redirect.guard';
import { EeocFormComponent } from './eeoc/eeoc-form/eeoc-form.component';
import { ModalComponent } from './modal/modal.component';
import { InputBoxComponent } from './web-components/input-box/input-box.component';
import { DropdownBoxComponent } from './web-components/dropdown-box/dropdown-box.component';
import { ChipBoxComponent } from './web-components/chip-box/chip-box.component';
import { CheckboxComponent } from './web-components/checkbox/checkbox.component';
import { EeocFormUsComponent } from './eeoc/eeoc-form-us/eeoc-form-us.component';
import { WebStyleTopBarComponent } from './web-style-top-bar/web-style-top-bar.component';
import { PreferencesConfirmedPageComponent } from './preferences-confirmed-page/preferences-confirmed-page.component';
import { JobPreferencesReadOnlyComponent } from './job-preferences/job-preferences-read-only/job-preferences-read-only.component';
import { JobPreferencesService } from './job-preferences/service/job-preferences.service';
import { GatherModule } from './gather/gather.module';
import { FiveStarRatingRadioButtonsComponent } from './five-star-rating-radio-buttons/five-star-rating-radio-buttons.component';
import { YesNoRadioButtonsComponent } from './yes-no-radio-buttons/yes-no-radio-buttons.component';
import { TextareaBoxComponent } from './web-components/textarea-box/textarea-box.component';
import { DoubleOptInConfirmComponent } from './talent-happy-path/double-opt-in-confirm/double-opt-in-confirm.component';
import { PreInterviewQuestionsComponent } from './pre-interview-questions/pre-interview-questions.component';
import { QuestionComponent } from './pre-interview-questions/question/question.component';
import { AnswerPreInterviewQuestionsComponent } from './pre-interview-questions/answer-pre-interview-questions/answer-pre-interview-questions.component';
import { PreInterviewQuestionsViewComponent } from './pre-interview-questions/pre-interview-questions-view/pre-interview-questions-view.component';
import { PreInterviewQuestionsEditComponent } from './pre-interview-questions/pre-interview-questions-edit/pre-interview-questions-edit.component';
import { QuestionEditBoxComponent } from './pre-interview-questions/pre-interview-questions-edit/question-edit-box/question-edit-box.component';
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatRadioModule } from "@angular/material/radio";
import { MatTooltipModule } from "@angular/material/tooltip";
import { QuickApplyModule } from "./talent-happy-path/quick-apply/quick-apply.module";
import { TalentProfileModule } from "./talent-happy-path/talent-profile/talent-profile.module";
import { InitialInterviewModule } from "./talent-happy-path/initial-interview/initial-interview.module";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from "@angular/material/input";
import { ThankYouComponent } from "./talent-happy-path/thank-you/thank-you.component";
import { MatButton, MatButtonModule } from '@angular/material/button';
import { JobPostingClosedComponent } from "./talent-happy-path/job-posting-closed/job-posting-closed.component";
import {VmsSubmittalModule} from "./talent-happy-path/vms-submittal/vms-submittal.module";
import { EmailCancellationComponent } from './talent-availability/email-cancellation/email-cancellation.component';
import {
  PayTransparencyResponseChartComponent
} from './talent-happy-path/initial-interview/pay-transparency/pay-transparency-response-chart/pay-transparency-response-chart.component';
import { MatIconModule } from "@angular/material/icon";
import { InfoContainerComponent } from "./talent-happy-path/shared/components/info-container/info-container.component";

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '../assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    TopBarComponent,
    SvgIconComponent,
    BadUrlComponent,
    DetailsBoxComponent,
    SuccessPageComponent,
    FooterComponent,
    EeocInfoBarComponent,
    JobPreferencesComponent,
    JobPreferencesSuccessPageComponent,
    LoginPageComponent,
    LoaderComponent,
    NumberValidationDirective,
    EeocFormComponent,
    ModalComponent,
    InputBoxComponent,
    DropdownBoxComponent,
    EeocFormUsComponent,
    ChipBoxComponent,
    CheckboxComponent,
    WebStyleTopBarComponent,
    PreferencesConfirmedPageComponent,
    JobPreferencesReadOnlyComponent,
    FiveStarRatingRadioButtonsComponent,
    YesNoRadioButtonsComponent,
    TextareaBoxComponent,
    DoubleOptInConfirmComponent,
    PreInterviewQuestionsComponent,
    QuestionComponent,
    AnswerPreInterviewQuestionsComponent,
    PreInterviewQuestionsViewComponent,
    PreInterviewQuestionsEditComponent,
    QuestionEditBoxComponent,
    ThankYouComponent,
    JobPostingClosedComponent,
    EmailCancellationComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgSelectModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatRadioModule,
    NoopAnimationsModule,
    ClickOutsideModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    FormsModule,
    GatherModule,
    MatTooltipModule,
    QuickApplyModule,
    TalentProfileModule,
    InitialInterviewModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    BrowserAnimationsModule,
    MatButton,
    MatButtonModule,
    MatInputModule,
    VmsSubmittalModule,
    PayTransparencyResponseChartComponent,
    MatIconModule,
    InfoContainerComponent
  ],
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation]
    },
    ApiService,
    DatePipe,
    NameFormatPipe,
    AuthService,
    AuthGuard,
    AuthRequiredGuard,
    RedirectGuard,
    StorageService,
    AppStateService,
    NavigationService,
    JobPreferencesService,
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
